import React from "react";

const CLS = {
  GREEN: "border-emerald-400 bg-emerald-400/20 text-emerald-300",
  AMBER: "border-amber-400 bg-amber-400/20 text-amber-300",
  RED: "border-rose-500 bg-rose-500/20 text-rose-300",
};

const slug = (p) => p.replace(/[^a-z0-9]+/gi, "-").toLowerCase().replace(/^-|-$/g, "");

function Tyre({ pos, tyre, onClick }) {
  const status = tyre?.health || tyre?.tread_status;
  const cls = tyre ? (CLS[status] || CLS.GREEN) : "border-dashed border-zinc-700 bg-zinc-900/60 text-zinc-600";
  const title = tyre
    ? `${pos}\n${tyre.serial_number} • ${tyre.current_tread_depth ?? "—"}mm • ${Math.round(tyre.running_hours || 0).toLocaleString()}h`
    : `${pos}\nEmpty`;
  return (
    <button
      data-testid={`tyre-slot-${slug(pos)}`}
      title={title}
      onClick={() => tyre && onClick?.(tyre)}
      className="group flex flex-col items-center transition-transform duration-150 hover:-translate-y-0.5"
    >
      <div className={`flex h-12 w-7 flex-col justify-center gap-[3px] rounded-[5px] border-2 px-1 ${cls} ${tyre ? "hover:brightness-125" : ""}`}>
        <span className="h-px bg-current opacity-50" />
        <span className="h-px bg-current opacity-50" />
        <span className="h-px bg-current opacity-50" />
      </div>
      <span className="mt-1 h-3 max-w-[3.6rem] truncate font-mono text-[9px] leading-none text-zinc-400" title={tyre ? tyre.serial_number : pos}>
        {tyre ? tyre.serial_number : pos}
      </span>
    </button>
  );
}

function Axle({ left, right, fitted, onSelect }) {
  return (
    <div className="grid grid-cols-[1fr_8rem_1fr] items-center">
      <div className="flex justify-end gap-1.5 pr-1">
        {left.map((p) => <Tyre key={p} pos={p} tyre={fitted[p]} onClick={onSelect} />)}
      </div>
      <div className="relative flex h-12 items-center border-x-2 border-zinc-700 bg-zinc-800">
        <div className="h-[3px] w-full bg-zinc-600" />
        <div className="absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-zinc-500 bg-zinc-700" />
      </div>
      <div className="flex justify-start gap-1.5 pl-1">
        {right.map((p) => <Tyre key={p} pos={p} tyre={fitted[p]} onClick={onSelect} />)}
      </div>
    </div>
  );
}

function ChassisBlock({ children, className = "" }) {
  return (
    <div className="grid grid-cols-[1fr_8rem_1fr] items-center">
      <span />
      <div className={`border-x-2 border-zinc-700 bg-zinc-800/70 text-center ${className}`}>{children}</div>
      <span />
    </div>
  );
}

// Parse compact axle codes (e.g. 1FL, 1FR, 2RLO, 2RLI, 2RRI, 2RRO) into axles.
// Codes are normalised (trimmed + upper-cased) so stray spaces/case never break the diagram.
function buildAxles(positions) {
  const map = {};
  for (const raw of positions) {
    const p = String(raw).trim().toUpperCase();
    const m = /^(\d+)([FR])([LR])([OI]?)$/.exec(p);
    if (!m) return null; // non-standard code -> fallback grid
    const [, axle, , side] = m;
    (map[axle] ??= { L: [], R: [] })[side].push(p);
  }
  // order: left side outer->inner (toward chassis), right side inner->outer
  const rank = (p) => (p.endsWith("O") ? 0 : p.endsWith("I") ? 1 : 0);
  const axleNums = Object.keys(map).map(Number).sort((a, b) => a - b);
  return axleNums.map((n) => {
    const a = map[String(n)];
    a.L.sort((x, y) => rank(x) - rank(y));
    a.R.sort((x, y) => rank(y) - rank(x));
    return { axle: n, ...a };
  });
}

// Canonical layouts by vehicle type — used when a vehicle's stored layout is missing or dirty.
const CANON = {
  "100T Dumper": ["1FL", "1FR", "2RLO", "2RLI", "2RRI", "2RRO"],
  "60T Dumper": ["1FL", "1FR", "2RLO", "2RLI", "2RRI", "2RRO", "3RLO", "3RLI", "3RRI", "3RRO"],
  "3-Axle Truck": ["1FL", "1FR", "2RLO", "2RLI", "2RRI", "2RRO", "3RLO", "3RLI", "3RRI", "3RRO"],
  "4-Axle Truck": ["1FL", "1FR", "2FL", "2FR", "3RLO", "3RLI", "3RRI", "3RRO", "4RLO", "4RLI", "4RRI", "4RRO"],
};

export default function TyreDiagram({ positions = [], fitted = {}, onSelect, vehicleType }) {
  const parsedOwn = positions.length ? buildAxles(positions) : null;
  const canon = CANON[vehicleType];
  // Prefer the vehicle's own layout. If it's non-standard, draw the canonical chassis
  // and remap fitted tyres onto it by position order so they still appear in the diagram.
  let axles = parsedOwn;
  let remap = null;
  if (!axles && canon) {
    axles = buildAxles(canon);
    if (axles && positions.length === canon.length) {
      remap = {};
      positions.forEach((p, i) => { remap[String(p).trim().toUpperCase()] = canon[i].toUpperCase(); });
    }
  }
  // Normalise fitted keys (and remap non-standard codes) so tyres map onto the diagram.
  const nf = React.useMemo(() => {
    const o = {};
    for (const [k, val] of Object.entries(fitted || {})) {
      const kk = String(k).trim().toUpperCase();
      o[remap && remap[kk] ? remap[kk] : kk] = val;
    }
    return o;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitted, JSON.stringify(remap)]);
  const bodyLabel = (vehicleType || "").includes("Truck") ? "CARGO BODY" : "DUMP BODY";

  if (!axles) {
    // Fallback: simple responsive grid for non-standard layouts
    return (
      <div className="rounded-sm border border-zinc-800 bg-zinc-950 p-6">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {positions.map((p) => (
            <div key={p} className="flex flex-col items-center gap-2">
              <Tyre pos={p} tyre={nf[String(p).trim().toUpperCase()]} onClick={onSelect} />
              <span className="text-center text-[10px] text-zinc-500">{p}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-sm border border-zinc-800 bg-zinc-950 p-6">
      <div className="mx-auto max-w-sm">
        <ChassisBlock className="rounded-t-2xl border-t-2 py-3">
          <span className="overline text-zinc-400">▲ CAB / FRONT</span>
        </ChassisBlock>

        {axles.map((a, i) => (
          <React.Fragment key={a.axle}>
            <Axle left={a.L} right={a.R} fitted={nf} onSelect={onSelect} />
            {i === 0 ? (
              <ChassisBlock className="py-6"><span className="overline text-zinc-500">{bodyLabel}</span></ChassisBlock>
            ) : i < axles.length - 1 ? (
              <ChassisBlock className="py-4" />
            ) : null}
          </React.Fragment>
        ))}

        <ChassisBlock className="rounded-b-2xl border-b-2 py-3">
          <span className="overline text-zinc-500">REAR ▼</span>
        </ChassisBlock>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-xs">
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-emerald-400" /> Healthy</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-amber-400" /> Monitor</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-rose-500" /> Critical</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm border border-dashed border-zinc-600" /> Empty</span>
      </div>
    </div>
  );
}
